/*
  Madewell GFRP vs Steel Cost + CO2 Calculator
  Version: 1.0

  What this does:
  - Keeps the existing Madewell-style comparison against SL62 / SL72 / SL82 / SL92.
  - Adds estimated CO2 emissions and CO2 savings.
  - Keeps all assumptions in CONFIG so they can be adjusted without rewriting the calculator.

  Important:
  This is an estimator, not an engineering design tool.
*/

(function () {
  "use strict";

  const CONFIG = {
    steelSheetAreaM2: 14.4, // Standard 6.0m x 2.4m sheet
    gfrpBarLengthM: 5.8,
    gfrpLapAllowanceM: 0.4,
    gfrpBarPrice: 6.5, // Madewell listed price for 10mm x 5.8m bar, excl. GST
    gfrpWeightKgPerM: 0.157, // Approx. 10mm GFRP weight based on 2,000 kg/m3 density
    mesh: {
      sl62: {
        label: "SL62",
        weightKgPerSheet: 33,
        gfrpSpacingM: 0.5,
        defaultPrice: 88
      },
      sl72: {
        label: "SL72",
        weightKgPerSheet: 41,
        gfrpSpacingM: 0.5,
        defaultPrice: 105
      },
      sl82: {
        label: "SL82",
        weightKgPerSheet: 52,
        gfrpSpacingM: 0.45,
        defaultPrice: 125
      },
      sl92: {
        label: "SL92",
        weightKgPerSheet: 65,
        gfrpSpacingM: 0.4,
        defaultPrice: 145
      }
    }
  };

  const $ = (id) => document.getElementById(id);

  const inputIds = [
    "gfrp-length",
    "gfrp-width",
    "gfrp-price-sl62",
    "gfrp-price-sl72",
    "gfrp-price-sl82",
    "gfrp-price-sl92",
    "gfrp-steel-co2-factor",
    "gfrp-gfrp-co2-factor",
    "gfrp-gfrp-transport-factor"
  ];

  function numberFromInput(id, fallback = 0) {
    const element = $(id);
    if (!element) return fallback;

    const value = Number.parseFloat(element.value);
    return Number.isFinite(value) && value >= 0 ? value : fallback;
  }

  function formatMoney(value, decimals = 0) {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals
    }).format(value);
  }

  function formatNumber(value, decimals = 0) {
    return new Intl.NumberFormat("en-AU", {
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals
    }).format(value);
  }

  function formatKg(value) {
    if (value >= 1000) {
      return `${formatNumber(value / 1000, 2)} t`;
    }

    return `${formatNumber(value, 0)} kg`;
  }

  function setText(id, value) {
    const element = $(id);
    if (element) element.textContent = value;
  }

  function setHTML(id, value) {
    const element = $(id);
    if (element) element.innerHTML = value;
  }

  function calculateBarsRequired(lengthM, widthM, spacingM) {
    if (!lengthM || !widthM || !spacingM) return 0;

    /*
      Calculates a two-way GFRP grid:
      - bars running along slab length, spaced across slab width
      - bars running along slab width, spaced across slab length
      - converts total linear metres into 5.8m bars with 400mm lap allowance
    */
    const barsAcrossWidth = Math.ceil(widthM / spacingM) + 1;
    const barsAcrossLength = Math.ceil(lengthM / spacingM) + 1;

    const totalLinearM = (barsAcrossWidth * lengthM) + (barsAcrossLength * widthM);
    const effectiveBarLengthM = CONFIG.gfrpBarLengthM - CONFIG.gfrpLapAllowanceM;

    return Math.ceil(totalLinearM / effectiveBarLengthM);
  }

  function calculate() {
    const lengthM = numberFromInput("gfrp-length");
    const widthM = numberFromInput("gfrp-width");
    const areaM2 = lengthM * widthM;

    const steelCo2Factor = numberFromInput("gfrp-steel-co2-factor", 1.92);
    const gfrpCo2FactorA1A3 = numberFromInput("gfrp-gfrp-co2-factor", 2.06);
    const gfrpTransportFactorA4 = numberFromInput("gfrp-gfrp-transport-factor", 0.093);

    const priceByMesh = {
      sl62: numberFromInput("gfrp-price-sl62", CONFIG.mesh.sl62.defaultPrice),
      sl72: numberFromInput("gfrp-price-sl72", CONFIG.mesh.sl72.defaultPrice),
      sl82: numberFromInput("gfrp-price-sl82", CONFIG.mesh.sl82.defaultPrice),
      sl92: numberFromInput("gfrp-price-sl92", CONFIG.mesh.sl92.defaultPrice)
    };

    setText("gfrp-area", `${formatNumber(areaM2, 1)} m²`);

    let bestSaving = 0;
    let bestCo2Saving = 0;

    Object.entries(CONFIG.mesh).forEach(([key, mesh]) => {
      const steelSheets = areaM2 > 0 ? Math.ceil(areaM2 / CONFIG.steelSheetAreaM2) : 0;
      const gfrpBars = calculateBarsRequired(lengthM, widthM, mesh.gfrpSpacingM);

      const steelCost = steelSheets * priceByMesh[key];
      const gfrpCost = gfrpBars * CONFIG.gfrpBarPrice;
      const materialSaving = steelCost - gfrpCost;
      const materialSavingPercent = steelCost > 0 ? (materialSaving / steelCost) * 100 : 0;

      const steelWeightKg = steelSheets * mesh.weightKgPerSheet;
      const gfrpWeightKg = gfrpBars * CONFIG.gfrpBarLengthM * CONFIG.gfrpWeightKgPerM;
      const weightSavingKg = steelWeightKg - gfrpWeightKg;

      const steelCo2Kg = steelWeightKg * steelCo2Factor;
      const gfrpCo2Kg = gfrpWeightKg * (gfrpCo2FactorA1A3 + gfrpTransportFactorA4);
      const co2SavingKg = steelCo2Kg - gfrpCo2Kg;
      const co2SavingPercent = steelCo2Kg > 0 ? (co2SavingKg / steelCo2Kg) * 100 : 0;

      const steelCostPerM2 = areaM2 > 0 ? steelCost / areaM2 : 0;
      const gfrpCostPerM2 = areaM2 > 0 ? gfrpCost / areaM2 : 0;

      bestSaving = Math.max(bestSaving, materialSaving);
      bestCo2Saving = Math.max(bestCo2Saving, co2SavingKg);

      setHTML(
        `saving-${key}`,
        `${formatMoney(materialSaving)}<br><small>${formatNumber(materialSavingPercent, 0)}% less</small><br><small>based on ${formatMoney(priceByMesh[key])}/sheet</small>`
      );

      setHTML(
        `weight-saving-${key}`,
        `${formatKg(weightSavingKg)}<br><small>Steel: ${formatKg(steelWeightKg)} vs GFRP: ${formatKg(gfrpWeightKg)}</small>`
      );

      setHTML(
        `co2-saving-${key}`,
        `${formatKg(co2SavingKg)} CO₂e<br><small>${formatNumber(co2SavingPercent, 0)}% less</small>`
      );

      setText(`steel-co2-${key}`, `${formatKg(steelCo2Kg)} CO₂e`);
      setText(`gfrp-co2-${key}`, `${formatKg(gfrpCo2Kg)} CO₂e`);

      setText(`sheets-${key}`, formatNumber(steelSheets));
      setHTML(
        `bars-${key}`,
        `${formatNumber(gfrpBars)}<br><small>(${Math.round(mesh.gfrpSpacingM * 1000)} c/c, 400 lap)</small>`
      );

      setHTML(
        `sqm-cost-${key}`,
        `Steel ${formatMoney(steelCostPerM2, 2)}/m²<br><small>vs GFRP ${formatMoney(gfrpCostPerM2, 2)}/m²</small>`
      );
    });

    setText("gfrp-best-saving", formatMoney(bestSaving));
    setText("gfrp-best-co2", `${formatKg(bestCo2Saving)} CO₂e`);
  }

  function init() {
    const calculator = $("gfrp-cost-co2-calculator");
    if (!calculator) return;

    inputIds.forEach((id) => {
      const input = $(id);
      if (!input) return;

      input.addEventListener("input", calculate);
      input.addEventListener("change", calculate);
    });

    calculate();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
